import React from 'react';

const TermsOfUse = () => {
    return (
        <main style={{ maxWidth: 800, margin: '0 auto', padding: '2rem' }}>
            <h1>The Crumb Explorer – Terms of Use</h1>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>

            <section>
                <h2>1. Overview</h2>
                <p>
                    These Terms of Use (“Terms”) govern your access to and use of The Crumb Explorer
                    (“App”), a Solana-based NFT and token explorer operated by
                    <strong> Kyle Hinton – The Simulacrum</strong> (“we”, “us”, or “our”).
                </p>
                <p>
                    By accessing or using the App, you agree to be bound by these Terms.
                    If you do not agree, you must not use the App.
                </p>
            </section>

            <section>
                <h2>2. Eligibility</h2>
                <p>
                    You may use the App only if you have the legal capacity to form a binding contract
                    in your jurisdiction and are not prohibited from using blockchain or crypto-related
                    services under applicable laws.
                </p>
            </section>

            <section>
                <h2>3. Nature of the Service</h2>
                <p>
                    The Crumb Explorer is an informational tool that allows you to view, explore,
                    and interact with public data on the Solana blockchain, including (but not limited to)
                    NFTs, tokens, and on-chain transactions. The App may also integrate with third-party
                    wallets and infrastructure providers.
                </p>
                <p>
                    We do <strong>not</strong>:
                </p>
                <ul>
                    <li>Provide financial, investment, or tax advice.</li>
                    <li>Custody your funds or private keys.</li>
                    <li>Guarantee the accuracy, completeness, or timeliness of on-chain or off-chain data.</li>
                </ul>
            </section>

            <section>
                <h2>4. Wallets and Security</h2>
                <p>
                    The App may allow you to connect a Solana wallet (e.g. Phantom, Solflare, or other
                    compatible wallets) to view assets or initiate on-chain transactions. Your wallet,
                    private keys, and seed phrases are controlled solely by you.
                </p>
                <p>
                    You are responsible for:
                </p>
                <ul>
                    <li>Maintaining the security of your wallet and devices.</li>
                    <li>Reviewing and approving any transactions you sign.</li>
                    <li>All activity that occurs using your wallet address when interacting with the App.</li>
                </ul>
                <p>
                    We will never request your private keys or seed phrase. Do not share them with anyone.
                </p>
            </section>

            <section>
                <h2>5. On-Chain Actions and Irreversibility</h2>
                <p>
                    Certain features of the App may expose functionality to perform actions on-chain,
                    such as transfers, burns, or metadata updates (where applicable).
                </p>
                <p>
                    Blockchain transactions are typically <strong>irreversible</strong>. Once submitted to the
                    Solana network and confirmed, they generally cannot be undone. You are solely
                    responsible for any on-chain actions you initiate through the App.
                </p>
            </section>

            <section>
                <h2>6. No Warranties</h2>
                <p>
                    The App is provided on an “AS IS” and “AS AVAILABLE” basis, without any warranties of
                    any kind, whether express or implied, including but not limited to implied warranties
                    of merchantability, fitness for a particular purpose, non-infringement, accuracy,
                    or availability.
                </p>
                <p>
                    We do not warrant that:
                </p>
                <ul>
                    <li>The App will be uninterrupted, secure, or error-free.</li>
                    <li>Any on-chain data, prices, or metadata displayed are correct or complete.</li>
                    <li>Any specific outcome will result from using the App.</li>
                </ul>
            </section>

            <section>
                <h2>7. Assumption of Risk</h2>
                <p>
                    By using the App, you acknowledge that blockchain and crypto assets carry inherent
                    risks, including but not limited to:
                </p>
                <ul>
                    <li>Volatility in asset values.</li>
                    <li>Smart contract bugs or exploits.</li>
                    <li>Network congestion, forks, or downtime.</li>
                    <li>Loss of access to wallets or private keys.</li>
                </ul>
                <p>
                    You assume full responsibility for any loss of funds, NFTs, or other digital assets,
                    and for any decisions you make based on information presented by the App.
                </p>
            </section>

            <section>
                <h2>8. Intellectual Property</h2>
                <p>
                    The App’s UI, branding, text, graphics, and other original content (excluding
                    third-party content and user-owned assets) are owned by
                    <strong> Kyle Hinton – The Simulacrum</strong> and are protected by applicable copyright,
                    trademark, and other laws. See the separate Copyright page for more details.
                </p>
            </section>

            <section>
                <h2>9. Third-Party Services</h2>
                <p>
                    The App may rely on or link to third-party services, such as RPC providers, wallet
                    adapters, analytics, or external websites. We do not control, endorse, or assume
                    responsibility for any third-party services. Your use of them is subject to their
                    own terms and policies.
                </p>
            </section>

            <section>
                <h2>10. Limitation of Liability</h2>
                <p>
                    To the maximum extent permitted by law, in no event will we be liable for any
                    indirect, incidental, special, consequential, or punitive damages, or any loss of
                    profits or revenues, whether incurred directly or indirectly, or any loss of data,
                    use, goodwill, or other intangible losses, resulting from:
                </p>
                <ul>
                    <li>Your access to or use of (or inability to access or use) the App.</li>
                    <li>Any conduct or content of any third party on the App or blockchain.</li>
                    <li>Any on-chain transaction you initiate or sign.</li>
                </ul>
            </section>

            <section>
                <h2>11. Changes to the App and These Terms</h2>
                <p>
                    We may modify or discontinue parts of the App at any time, and we may update these
                    Terms from time to time. If we make material changes, we may publish an updated
                    version with a new “Last updated” date. Your continued use of the App after changes
                    become effective constitutes acceptance of the revised Terms.
                </p>
            </section>

            <section>
                <h2>12. Governing Law</h2>
                <p>
                    These Terms are governed by the laws of the jurisdiction in which
                    <strong> Kyle Hinton – The Simulacrum</strong> operates, without regard to its conflict of
                    law principles. You agree to submit to the personal jurisdiction of competent
                    courts in that jurisdiction.
                </p>
            </section>

            <section>
                <h2>13. Contact</h2>
                <p>
                    For questions about these Terms, you can contact:
                </p>
                <p>
                    Email: <a href="mailto:kyle-hinton@outlook.com">kyle-hinton@outlook.com</a><br />
                    Website: <a href="https://thesimulacrum.xyz/" target="_blank" rel="noreferrer">
                    https://thesimulacrum.xyz/
                </a>
                </p>
            </section>
        </main>
    );
};

export default TermsOfUse;
